/**
 * @file useChatSocket.js
 * @brief Manages the direct chat websocket connection used for realtime conversation updates.
 */
// hooks/useChatSocket.js
//
// Manages a WebSocket connection to the chat consumer.
// Falls back to polling if the socket fails to connect or disconnects.
//
// Usage:
//   const { sendMessage, markRead, connected } = useChatSocket({
//     listingId, otherUserId, token,
//     onMessage,   // (msg) => void  — called when a new message arrives
//     onRead,      // ({ reader_id }) => void — called when read receipt arrives
//   });

import { useEffect, useRef, useState, useCallback } from "react";

const WS_BASE = process.env.REACT_APP_WS_BASE_URL || "ws://localhost:7000";

const RECONNECT_DELAYS = [1000, 2000, 5000, 10000]; // ms, capped at last value

/**
 * Open and maintain the websocket used for a listing conversation.
 * @param {Object} params Hook parameters.
 * @param {string} params.listingId Listing identifier bound to the conversation room.
 * @param {string} params.otherUserId Counterparty profile identifier.
 * @param {string} params.token Bearer token used for websocket authentication.
 * @param {Function} params.onMessage Callback invoked for incoming chat messages.
 * @param {Function} params.onRead Callback invoked for read receipt events.
 * @param {Function} params.onTransaction Callback invoked for transaction-related socket events.
 * @returns {{sendMessage: Function, markRead: Function, connected: boolean}} Socket helpers and connection state.
 */
export function useChatSocket({ listingId, otherUserId, token, onMessage, onRead, onTransaction }) {
    const socketRef       = useRef(null);
    const reconnectTimer  = useRef(null);
    const reconnectCount  = useRef(0);
    const unmounted       = useRef(false);
    const onMessageRef    = useRef(onMessage);
    const onReadRef       = useRef(onRead);
    const onTransactionRef = useRef(onTransaction);
    const [connected, setConnected] = useState(false);

    useEffect(() => { onMessageRef.current = onMessage; }, [onMessage]);
    useEffect(() => { onReadRef.current = onRead; }, [onRead]);
    useEffect(() => { onTransactionRef.current = onTransaction; }, [onTransaction]);

    const connect = useCallback(() => {
        if (unmounted.current || !token || !listingId || !otherUserId) return;

        const url = `${WS_BASE}/ws/messages/${listingId}/${otherUserId}/?token=${token}`;
        const ws  = new WebSocket(url);
        socketRef.current = ws;

        ws.onopen = () => {
            if (unmounted.current) { ws.close(); return; }
            reconnectCount.current = 0;
            setConnected(true);
        };

        ws.onmessage = (event) => {
            if (unmounted.current) return;
            try {
                const data = JSON.parse(event.data);
                if (data.type === "chat.message") onMessageRef.current?.(data.message);
                if (data.type === "chat.read")    onReadRef.current?.(data);
                if (data.type === "chat.transaction") onTransactionRef.current?.(data);
                if (data.type === "chat.error")   console.warn("[WS]", data.detail);
            } catch (e) {
                console.error("[WS] Failed to parse message", e);
            }
        };

        ws.onerror = (e) => {
            console.warn("[WS] Error", e);
        };

        ws.onclose = (event) => {
            if (unmounted.current) return;
            setConnected(false);

            // 4001 = unauthenticated, 4003 = forbidden — don't reconnect
            if (event.code === 4001 || event.code === 4003) {
                console.warn("[WS] Auth error, not reconnecting", event.code);
                return;
            }

            // Exponential backoff reconnect
            const delay = RECONNECT_DELAYS[
                Math.min(reconnectCount.current, RECONNECT_DELAYS.length - 1)
            ];
            reconnectCount.current += 1;
            reconnectTimer.current = setTimeout(connect, delay);
        };
    }, [listingId, otherUserId, token]);

    useEffect(() => {
        unmounted.current = false;
        connect();

        return () => {
            unmounted.current = true;
            clearTimeout(reconnectTimer.current);
            socketRef.current?.close();
        };
    }, [connect]);

    const sendMessage = useCallback((content) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: "message", content }));
            return true;  // sent via WS
        }
        return false;  // caller should fall back to REST
    }, []);

    const markRead = useCallback(() => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: "read" }));
        }
    }, []);

    return { sendMessage, markRead, connected };
}